import { Dispatch, SetStateAction, useEffect, useReducer } from 'react';

type ActiveItemsState = {
  previousActiveItemIndex: number | null;
  activeItemIndex: number | null;
  previousNestedItemIndex: number | null;
  activeNestedItemIndex: number | null;
};

type ActiveItemsAction = {
  type: 'activeItemIndex' | 'activeNestedItemIndex';
  value: number | null;
};

const activeItemsReducer = (
  state: ActiveItemsState,
  action: ActiveItemsAction
): ActiveItemsState => {
  switch (action.type) {
    case 'activeItemIndex':
      if (action.value === state.activeItemIndex) {
        return state;
      }

      if (action.value === null) {
        return {
          previousActiveItemIndex: state.activeItemIndex,
          activeItemIndex: action.value,
          previousNestedItemIndex: state.activeNestedItemIndex,
          activeNestedItemIndex: null,
        };
      }

      return {
        previousActiveItemIndex: state.activeItemIndex,
        activeItemIndex: action.value,
        previousNestedItemIndex: null,
        activeNestedItemIndex: null,
      };

    case 'activeNestedItemIndex':
      if (action.value === state.activeNestedItemIndex) {
        return state;
      }
      return {
        ...state,
        previousNestedItemIndex: state.activeNestedItemIndex,
        activeNestedItemIndex: action.value,
      };

    default:
      throw new Error('Unknown action found!');
  }
};

const activeItemsInitialState = (activeItemIndex: number | null): ActiveItemsState => ({
  activeItemIndex,
  previousActiveItemIndex: null,
  activeNestedItemIndex: null,
  previousNestedItemIndex: null,
});

type NavItemClickHandler = (newIndex: number) => () => void;
type NestedNavItemClickHandler = (newIndex: number | null) => void;
type SetActiveItemIndex =
  | ((index: number | null) => void)
  | Dispatch<SetStateAction<number | null>>;

export const useActiveItems = (
  activeItemIndex: number | null,
  setActiveItemIndex: SetActiveItemIndex
): [ActiveItemsState, NavItemClickHandler, NestedNavItemClickHandler] => {
  const [activeItemsInfo, activeInfoDispatcher] = useReducer(
    activeItemsReducer,
    activeItemsInitialState(activeItemIndex)
  );

  useEffect(() => {
    activeInfoDispatcher({ type: 'activeItemIndex', value: activeItemIndex });
  }, [activeItemIndex]);

  const onClickNavItem = (index: number) => (): void => {
    // If we have clicked on the same navItem again, close it, otherwise open the
    // new navItem.
    if (activeItemsInfo.activeItemIndex === index) {
      setActiveItemIndex(null);
    } else {
      setActiveItemIndex(index);
    }
  };

  const onClickNestedNavItem = (newIndex: number | null, closeIfActive = false): void =>
    activeInfoDispatcher({
      type: 'activeNestedItemIndex',
      value:
        closeIfActive && activeItemsInfo.activeNestedItemIndex === newIndex
          ? null
          : newIndex,
    });

  return [activeItemsInfo, onClickNavItem, onClickNestedNavItem];
};
